import { useState, useRef, useEffect, useCallback, useContext } from "react";
import "../../styles/panel/LoginPage.css";
import {
  checkPhone,
  sendOtp,
  verifyOtp,
  loginWithPassword,
  completeRegistration,
  verifyResetOtp,
  resetPasswordForLogIn,
} from "../../api/auth";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { AuthContext } from "../../context/AuthContext";
import flowerHeader from "../../assets/flower-header.svg";
import flowerBody from "../../assets/flower-body.svg";
import { swalError, swalSuccess } from "../../utils/swalAlert";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 120;
const MIN_PASSWORD = 8;
const REDIRECT_DELAY = 1500;

const toFa = (n) => String(n).replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);
// تبدیل ارقام فارسی و عربی به انگلیسی
const toEn = (s) =>
  String(s)
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d))
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d));

// پیام خطا را از پاسخ DRF درمی‌آورد: {detail}, {error}, {message} یا خطای فیلدها
const errMsg = (error, fallback) => {
  const d = error.response?.data;
  if (!d || typeof d !== "object") return fallback;
  const direct = d.detail || d.error || d.message;
  if (typeof direct === "string") return direct;
  const first = Object.values(d)[0];
  const text = Array.isArray(first) ? first[0] : first;
  return typeof text === "string" ? text : fallback;
};

export default function LoginPage() {
  const { login, getHomePath } = useContext(AuthContext);
  const navigate = useNavigate();

  // login | register | forgot | reset | done
  const [step, setStep] = useState("login");
  // توکن موقتی که بعد از تایید OTP فراموشی رمز عبور از سرور می‌آید
  const [resetToken, setResetToken] = useState("");
  // پیام موفقیت (مثلاً بعد از تغییر رمز عبور)
  const [notice, setNotice] = useState("");
  // otp | password
  const [loginMethod, setLoginMethod] = useState("otp");

  // phone / otp
  const [phone, setPhone] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [phoneError, setPhoneError] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(""));
  const [otpError, setOtpError] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const [timerActive, setTimerActive] = useState(false);
  const [resending, setResending] = useState(false);
  // null = هنوز بررسی نشده | true = کاربر موجود | false = کاربر جدید
  const [userExists, setUserExists] = useState(null);

  // password login
  const [loginPassword, setLoginPassword] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [passwordLoginError, setPasswordLoginError] = useState("");
  const [passwordLoggingIn, setPasswordLoggingIn] = useState(false);

  // registration
  const [registrationToken, setRegistrationToken] = useState(""); // از verifyOtp برای کاربر جدید
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const otpRefs = useRef([]);
  const phoneInputRef = useRef(null);
  const verifyingRef = useRef(false);
  const redirectRef = useRef(null);

  const phoneValid = /^9\d{9}$/.test(phone);
  const otpComplete = otp.every((d) => d.length === 1);

  useEffect(() => () => clearTimeout(redirectRef.current), []);

  // ---- resend timer ----
  useEffect(() => {
    if (!timerActive) return;
    if (secondsLeft <= 0) {
      setTimerActive(false);
      return;
    }
    const t = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timerActive, secondsLeft]);

  const startTimer = useCallback(() => {
    setSecondsLeft(RESEND_SECONDS);
    setTimerActive(true);
  }, []);

  // ---- finish (login + redirect) ----
  // data = { access, refresh }
  // login() توکن‌ها را ذخیره و پروفایل را می‌گیرد؛ هدایت را خودمان بعد از انیمیشن انجام می‌دهیم.
  const finishLogin = async (data) => {
    const profile = await login(data, { redirect: false });
    setTimerActive(false);
    setStep("done");
    redirectRef.current = setTimeout(
      () => navigate(getHomePath(profile)),
      REDIRECT_DELAY,
    );
  };

  // ---- phone ----
  const handlePhoneChange = (e) => {
    let digits = toEn(e.target.value).replace(/[^0-9]/g, "");
    if (digits.startsWith("0")) {
      digits = digits.slice(1);
    }
    setPhone(digits.slice(0, 10));
    setUserExists(null);
    setLoginMethod("otp");
    setLoginPassword("");
    setPhoneError(false);
    setPasswordLoginError("");
    setNotice("");
  };

  const handleSendCode = async () => {
    if (!phoneValid || sendingCode) return;

    setSendingCode(true);

    try {
      // فقط اگر هنوز شماره بررسی نشده، check-phone را صدا بزن
      // (اگر کاربر موجود دستی به «ورود با کد» برگشته، بررسی تکرار نمی‌شود)
      if (userExists === null) {
        const res = await checkPhone(phone);
        const exists = Boolean(
          res.data?.exists ?? res.data?.user_exists ?? res.data?.is_registered,
        );
        setUserExists(exists);

        if (exists) {
          // کاربر از قبل وجود دارد → صفحه رمز عبور
          setLoginMethod("password");
          setLoginPassword("");
          setPasswordLoginError("");
          setPhoneError(false);
          return;
        }
      }

      // کاربر جدید (یا انتخاب دستی ورود با کد) → ارسال OTP
      await sendOtp(phone);

      setCodeSent(true);
      setPhoneError(false);
      setOtp(Array(OTP_LENGTH).fill(""));
      setOtpError(false);
      setOtpErrorMessage("");

      startTimer();

      setTimeout(() => {
        otpRefs.current[0]?.focus();
      }, 400);
    } catch (error) {
      console.error(error);
      setPhoneError(true);
    } finally {
      setSendingCode(false);
    }
  };

  const handleEdit = () => {
    setCodeSent(false);
    setTimerActive(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpError(false);
    setOtpErrorMessage("");
    setTimeout(() => phoneInputRef.current?.focus(), 0);
  };

  const switchMethod = (method) => {
    setLoginMethod(method);
    setPhoneError(false);
    setPasswordLoginError("");
    setOtpError(false);
    setOtpErrorMessage("");
    setLoginPassword("");
    if (method === "password") {
      setCodeSent(false);
      setTimerActive(false);
      setOtp(Array(OTP_LENGTH).fill(""));
    }
  };

  // ---- otp ----
  const handleOtpChange = (index, rawValue) => {
    const value = toEn(rawValue)
      .replace(/[^0-9]/g, "")
      .slice(-1);
    setOtp((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setOtpError(false);
    if (value && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const digits = (
      toEn(e.clipboardData.getData("text")).match(/\d/g) || []
    ).slice(0, OTP_LENGTH);
    if (!digits.length) return;
    setOtp((prev) => {
      const next = [...prev];
      digits.forEach((d, i) => {
        next[i] = d;
      });
      return next;
    });
    setOtpError(false);
    const lastIndex = Math.min(digits.length, OTP_LENGTH) - 1;
    otpRefs.current[lastIndex]?.focus();
  };

  const handleVerify = async () => {
    if (!otpComplete || verifyingRef.current) return;
    verifyingRef.current = true;
    setSubmitting(true);

    try {
      setOtpError(false);
      setOtpErrorMessage("");

      const response = await verifyOtp(phone, otp.join(""));
      setTimerActive(false);

      if (response.data?.needs_registration) {
        // کاربر جدید (یا ثبت‌نام ناتمام): هنوز login نکن، فقط توکن ثبت‌نام را نگه دار
        setRegistrationToken(response.data.registration_token);
        setFormError("");
        setStep("register");
        return;
      }

      await finishLogin(response.data);
    } catch (error) {
      console.error("OTP Error:", error);
      setOtpError(true);
      setOtpErrorMessage(errMsg(error, "خطایی در تایید کد رخ داده است."));
    } finally {
      verifyingRef.current = false;
      setSubmitting(false);
    }
  };

  // ---- auto-submit when OTP is fully entered (typed or pasted) ----
  useEffect(() => {
    if (step === "login" && loginMethod === "otp" && codeSent && otpComplete) {
      handleVerify();
    } else if (step === "forgot" && otpComplete) {
      handleVerifyReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otpComplete]);

  const handleResend = async () => {
    if (timerActive || resending) return;

    setResending(true);

    try {
      await sendOtp(phone);

      setOtp(Array(OTP_LENGTH).fill(""));
      setOtpError(false);
      setOtpErrorMessage("");
      startTimer();
      otpRefs.current[0]?.focus();
    } catch (error) {
      console.error(error);
      setOtpError(true);
      setOtpErrorMessage("ارسال دوباره کد با خطا مواجه شد.");
    } finally {
      setResending(false);
    }
  };

  // ---- forgot password ----
  // 1) ارسال کد به شماره و رفتن به صفحه‌ی وارد کردن کد
  const handleForgotPassword = async () => {
    if (!phoneValid || sendingCode) return;

    setSendingCode(true);
    setPasswordLoginError("");

    try {
      await sendOtp(phone);

      setOtp(Array(OTP_LENGTH).fill(""));
      setOtpError(false);
      setOtpErrorMessage("");
      setPassword("");
      setPasswordConfirm("");
      setShowPassword(false);
      setFormError("");
      setNotice("");
      setStep("forgot");
      startTimer();

      setTimeout(() => otpRefs.current[0]?.focus(), 400);
    } catch (error) {
      console.error(error);
      setPasswordLoginError(errMsg(error, "ارسال کد با خطا مواجه شد."));
    } finally {
      setSendingCode(false);
    }
  };

  // 2) تایید کد؛ در صورت موفقیت سرور reset_token می‌دهد (کاربر هنوز لاگین نمی‌شود)
  const handleVerifyReset = async () => {
    if (!otpComplete || verifyingRef.current) return;
    verifyingRef.current = true;
    setSubmitting(true);

    try {
      setOtpError(false);
      setOtpErrorMessage("");

      const response = await verifyResetOtp(phone, otp.join(""));
      setResetToken(response.data?.access);
      setTimerActive(false);
      setFormError("");
      setStep("reset");
    } catch (error) {
      console.error("Reset OTP Error:", error);
      setOtpError(true);
      setOtpErrorMessage(errMsg(error, "خطایی در تایید کد رخ داده است."));
    } finally {
      verifyingRef.current = false;
      setSubmitting(false);
    }
  };

  // 3) ثبت رمز عبور جدید
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (password.length < MIN_PASSWORD) {
      setFormError(`رمز عبور باید حداقل ${toFa(MIN_PASSWORD)} کاراکتر باشد.`);
      return;
    }
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setFormError("رمز عبور باید شامل حروف انگلیسی و عدد باشد.");
      return;
    }
    if (password !== passwordConfirm) {
      setFormError("رمز عبور و تکرار آن یکسان نیستند.");
      return;
    }

    setSubmitting(true);
    setFormError("");

    try {
      const res = await resetPasswordForLogIn({
        phone_number: phone,
        reset_token: resetToken,
        password,
        password_confirm: passwordConfirm,
      });

      // پیام موفقیت
      await swalSuccess({
        title: "رمز عبور تغییر کرد",
        text: res.data?.access
          ? "رمز عبور شما با موفقیت تغییر کرد. در حال ورود به حساب هستید."
          : "رمز عبور شما با موفقیت تغییر کرد. حالا می‌توانید با رمز جدید وارد شوید.",
        confirmButtonText: res.data?.access ? "ادامه" : "ورود",
        allowOutsideClick: false,
      });

      if (res.data?.access) {
        // سرور بعد از تغییر رمز، توکن هم داده → مستقیم وارد شو
        await finishLogin(res.data);
      } else {
        // در غیر این صورت به صفحه‌ی ورود با رمز عبور برگرد
        setPassword("");
        setPasswordConfirm("");
        setResetToken("");
        setLoginPassword("");
        setLoginMethod("password");
        setUserExists(true);
        setStep("login");
      }
    } catch (error) {
      console.error(error);
      setFormError(errMsg(error, "تغییر رمز عبور با خطا مواجه شد."));
    } finally {
      setSubmitting(false);
    }
  };

  const backToPasswordLogin = () => {
    setTimerActive(false);
    setOtp(Array(OTP_LENGTH).fill(""));
    setOtpError(false);
    setOtpErrorMessage("");
    setFormError("");
    setStep("login");
    setLoginMethod("password");
  };

  // ---- password login ----
  const handlePasswordLogin = async () => {
    if (!phoneValid || !loginPassword || passwordLoggingIn) return;

    setPasswordLoggingIn(true);
    setPasswordLoginError("");

    try {
      const response = await loginWithPassword(phone, loginPassword);
      await finishLogin(response.data);
    } catch (error) {
      console.error(error);
      setPasswordLoginError(
        errMsg(error, "شماره موبایل یا رمز عبور اشتباه است."),
      );
    } finally {
      setPasswordLoggingIn(false);
    }
  };

  // ---- registration ----
  const handleRegister = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!firstName.trim() || !lastName.trim()) {
      setFormError("نام و نام خانوادگی را وارد کنید.");
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setFormError(`رمز عبور باید حداقل ${toFa(MIN_PASSWORD)} کاراکتر باشد.`);
      return;
    }
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) {
      setFormError("رمز عبور باید شامل حروف انگلیسی و عدد باشد.");
      return;
    }
    if (password !== passwordConfirm) {
      setFormError("رمز عبور و تکرار آن یکسان نیستند.");
      return;
    }

    setSubmitting(true);
    setFormError("");

    try {
      const res = await completeRegistration({
        phone_number: phone,
        registration_token: registrationToken,
        password,
        password_confirm: passwordConfirm,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
      });

      // پاسخ: { access, refresh }
      await finishLogin(res.data);
    } catch (error) {
      console.error(error);
      setFormError(errMsg(error, "ثبت اطلاعات با خطا مواجه شد."));
    } finally {
      setSubmitting(false);
    }
  };

  const timerLabel = () => {
    const m = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
    const s = String(secondsLeft % 60).padStart(2, "0");
    return toFa(`${m}:${s}`);
  };

  return (
    <div className="login-page" dir="rtl">
      <img src={flowerHeader} alt="" className="login-background-1" />
      <img src={flowerBody} alt="" className="login-background-2" />
      <div className="login-glow" />

      <div className="auth-card">
        {/* ================= LOGIN ================= */}
        {step === "login" && (
          <div className="form-wrap">
            <div className="auth-title">ورود / ثبت نام</div>
            <div className="auth-sub">
              {notice || "شماره موبایل خود را وارد کنید."}
            </div>

            {/* PHONE */}
            <div className="field-label">
              <span>شماره موبایل</span>
              {codeSent && loginMethod === "otp" && (
                <button
                  type="button"
                  className="edit-link"
                  onClick={handleEdit}
                >
                  ویرایش
                </button>
              )}
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (loginMethod === "otp") handleSendCode();
                else handlePasswordLogin();
              }}
            >
              <div className="phone-row">
                <div className={`phone-field${codeSent ? " locked" : ""}`}>
                  <span className="phone-cc">98+</span>
                  <input
                    ref={phoneInputRef}
                    type="tel"
                    placeholder="912 345 6789"
                    maxLength={11}
                    inputMode="numeric"
                    value={phone}
                    disabled={codeSent && loginMethod === "otp"}
                    onChange={handlePhoneChange}
                  />
                </div>
                {loginMethod === "otp" && !codeSent && (
                  <button
                    type="button"
                    className="send-inline"
                    disabled={!phoneValid || sendingCode}
                    onClick={handleSendCode}
                  >
                    {sendingCode ? (
                      <span className="btn-spinner" aria-label="در حال ارسال" />
                    ) : (
                      "ادامه"
                    )}
                  </button>
                )}
              </div>

              {loginMethod === "password" && (
                <>
                  <div className="field-label field-gap">
                    <span>رمز عبور</span>
                    <button
                      type="button"
                      className="edit-link"
                      disabled={sendingCode}
                      onClick={handleForgotPassword}
                    >
                      {sendingCode ? "در حال ارسال کد..." : "فراموشی رمز عبور"}
                    </button>
                  </div>
                  <div className="text-field">
                    <input
                      type={showLoginPassword ? "text" : "password"}
                      autoComplete="current-password"
                      autoFocus={userExists === true}
                      value={loginPassword}
                      onChange={(e) => {
                        setLoginPassword(e.target.value);
                        setPasswordLoginError("");
                      }}
                    />
                    <button
                      type="button"
                      className="toggle-visibility"
                      onClick={() => setShowLoginPassword((v) => !v)}
                    >
                      {showLoginPassword ? "مخفی" : "نمایش"}
                    </button>
                  </div>
                  <div
                    className={`hint-row${passwordLoginError ? " show" : ""}`}
                  >
                    {passwordLoginError}
                  </div>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={
                      !phoneValid || !loginPassword || passwordLoggingIn
                    }
                  >
                    {passwordLoggingIn ? (
                      <span className="btn-spinner" aria-label="در حال ورود" />
                    ) : (
                      <span>ورود</span>
                    )}
                  </button>
                </>
              )}
            </form>

            {loginMethod === "otp" && (
              <>
                <div className={`hint-row${phoneError ? " show" : ""}`}>
                  شماره موبایل را درست وارد کنید.
                </div>

                {/* OTP */}
                <form
                  className={`otp-section${codeSent ? " open" : ""}`}
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleVerify();
                  }}
                >
                  <div className="field-label">
                    <span>کد تایید</span>
                  </div>
                  <div className="otp-boxes" onPaste={handleOtpPaste}>
                    {otp.map((digit, i) => (
                      <input
                        key={i}
                        ref={(el) => (otpRefs.current[i] = el)}
                        type="text"
                        maxLength={1}
                        inputMode="numeric"
                        autoComplete={i === 0 ? "one-time-code" : "off"}
                        className={`${digit ? "filled" : ""}${otpError ? " error" : ""}`}
                        value={digit}
                        onChange={(e) => handleOtpChange(i, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      />
                    ))}
                  </div>
                  <div className={`hint-row${otpError ? " show" : ""}`}>
                    {otpErrorMessage}
                  </div>
                  <div className="resend-row">
                    {timerActive ? (
                      <>
                        <span>ارسال مجدد کد تا</span>
                        <span className="timer-num">{timerLabel()}</span>
                      </>
                    ) : (
                      <button
                        type="button"
                        className={`resend-link${resending ? " disabled" : ""}`}
                        onClick={handleResend}
                      >
                        {resending ? (
                          <span
                            className="btn-spinner-dark"
                            aria-label="در حال ارسال"
                          />
                        ) : (
                          "ارسال دوباره"
                        )}
                      </button>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={!(codeSent && otpComplete) || submitting}
                  >
                    <span>تایید و ورود</span>

                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M19 12H5M11 6l-6 6 6 6" />
                    </svg>
                  </button>
                </form>
              </>
            )}

            {/* فقط برای کاربر موجود نمایش داده می‌شود (بعد از check-phone) */}
            {userExists === true && (
              <button
                type="button"
                className="method-switch"
                onClick={() => {
                  if (loginMethod === "otp") {
                    switchMethod("password");
                  } else {
                    // از رمز عبور به OTP: مستقیم کد ارسال شود و باکس‌های OTP باز شوند
                    switchMethod("otp");
                    handleSendCode();
                  }
                }}
              >
                {loginMethod === "otp"
                  ? "ورود با رمز عبور"
                  : "ورود با کد یکبار مصرف"}
              </button>
            )}

            <div className="legal-note">
              با ورود، <a href="/rules">قوانین و مقررات</a> را می‌پذیرید.
            </div>
          </div>
        )}

        {/* ================= FORGOT PASSWORD (OTP) ================= */}
        {step === "forgot" && (
          <form
            className="form-wrap"
            onSubmit={(e) => {
              e.preventDefault();
              handleVerifyReset();
            }}
          >
            <div className="auth-title">فراموشی رمز عبور</div>
            <div className="auth-sub">
              کد تایید ارسال‌شده به شماره {toFa(`0${phone}`)} را وارد کنید.
            </div>

            <div className="field-label">
              <span>کد تایید</span>
              <button
                type="button"
                className="edit-link"
                onClick={backToPasswordLogin}
              >
                بازگشت
              </button>
            </div>
            <div className="otp-boxes" onPaste={handleOtpPaste}>
              {otp.map((digit, i) => (
                <input
                  key={i}
                  ref={(el) => (otpRefs.current[i] = el)}
                  type="text"
                  maxLength={1}
                  inputMode="numeric"
                  autoComplete={i === 0 ? "one-time-code" : "off"}
                  className={`${digit ? "filled" : ""}${otpError ? " error" : ""}`}
                  value={digit}
                  onChange={(e) => handleOtpChange(i, e.target.value)}
                  onKeyDown={(e) => handleOtpKeyDown(i, e)}
                />
              ))}
            </div>
            <div className={`hint-row${otpError ? " show" : ""}`}>
              {otpErrorMessage}
            </div>
            <div className="resend-row">
              {timerActive ? (
                <>
                  <span>ارسال مجدد کد تا</span>
                  <span className="timer-num">{timerLabel()}</span>
                </>
              ) : (
                <button
                  type="button"
                  className={`resend-link${resending ? " disabled" : ""}`}
                  onClick={handleResend}
                >
                  {resending ? (
                    <span
                      className="btn-spinner-dark"
                      aria-label="در حال ارسال"
                    />
                  ) : (
                    "ارسال دوباره"
                  )}
                </button>
              )}
            </div>

            <button
              type="submit"
              className="btn-primary"
              disabled={!otpComplete || submitting}
            >
              {submitting ? (
                <span className="btn-spinner" aria-label="در حال بررسی" />
              ) : (
                <span>تایید کد</span>
              )}
            </button>
          </form>
        )}

        {/* ================= RESET PASSWORD ================= */}
        {step === "reset" && (
          <form className="form-wrap" onSubmit={handleResetPassword}>
            <div className="auth-title">رمز عبور جدید</div>
            <div className="auth-sub">
              یک رمز عبور جدید برای ورودهای بعدی تعیین کنید.
            </div>

            <div className="field-label">
              <span>رمز عبور جدید</span>
            </div>
            <div className="text-field">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                autoFocus
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFormError("");
                }}
              />
              <button
                type="button"
                className="toggle-visibility"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? "مخفی" : "نمایش"}
              </button>
            </div>

            <div className="field-label field-gap">
              <span>تکرار رمز عبور جدید</span>
            </div>
            <div className="text-field">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(e) => {
                  setPasswordConfirm(e.target.value);
                  setFormError("");
                }}
              />
            </div>

            <div className={`hint-row${formError ? " show" : ""}`}>
              {formError}
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={!password || !passwordConfirm || submitting}
            >
              {submitting ? (
                <span className="btn-spinner" aria-label="در حال ثبت" />
              ) : (
                <span>ثبت رمز عبور</span>
              )}
            </button>
          </form>
        )}

        {/* ================= REGISTER ================= */}
        {step === "register" && (
          <form className="form-wrap" onSubmit={handleRegister}>
            <div className="auth-title">تکمیل ثبت نام</div>
            <div className="auth-sub">
              اطلاعات خود را وارد کنید و یک رمز عبور برای ورودهای بعدی تعیین
              کنید.
            </div>

            <div className="field-label">
              <span>نام</span>
            </div>
            <div className="text-field">
              <input
                type="text"
                autoComplete="given-name"
                autoFocus
                value={firstName}
                onChange={(e) => {
                  setFirstName(e.target.value);
                  setFormError("");
                }}
              />
            </div>

            <div className="field-label field-gap">
              <span>نام خانوادگی</span>
            </div>
            <div className="text-field">
              <input
                type="text"
                autoComplete="family-name"
                value={lastName}
                onChange={(e) => {
                  setLastName(e.target.value);
                  setFormError("");
                }}
              />
            </div>

            <div className="field-label field-gap">
              <span>رمز عبور</span>
            </div>
            <div className="text-field">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setFormError("");
                }}
              />
              <button
                type="button"
                className="toggle-visibility"
                onClick={() => setShowPassword((v) => !v)}
              >
                {showPassword ? "مخفی" : "نمایش"}
              </button>
            </div>

            <div className="field-label field-gap">
              <span>تکرار رمز عبور</span>
            </div>
            <div className="text-field">
              <input
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                value={passwordConfirm}
                onChange={(e) => {
                  setPasswordConfirm(e.target.value);
                  setFormError("");
                }}
              />
            </div>

            <div className={`hint-row${formError ? " show" : ""}`}>
              {formError}
            </div>
            <button
              type="submit"
              className="btn-primary"
              disabled={
                !firstName.trim() ||
                !lastName.trim() ||
                !password ||
                !passwordConfirm ||
                submitting
              }
            >
              {submitting ? (
                <span className="btn-spinner" aria-label="در حال ثبت" />
              ) : (
                <span>تکمیل ثبت نام</span>
              )}
            </button>
          </form>
        )}

        {/* ================= DONE ================= */}
        {step === "done" && (
          <div className="success-pane show">
            <div className="success-icon">
              <svg
                className="success-check-svg"
                viewBox="0 0 52 52"
                fill="none"
              >
                <circle className="success-circle" cx="26" cy="26" r="23" />

                <path className="success-check" d="M15 27L22 34L37 18" />
              </svg>
            </div>

            <div className="success-title">خوش آمدید!</div>
            <div className="success-sub">ورود شما با موفقیت انجام شد.</div>
          </div>
        )}
      </div>
    </div>
  );
}
